import clsx from 'clsx';
import { format } from 'date-fns';
import { identityDisplay } from './subject';
import type { CardTone, CustomerCardProps } from './types';

const TONE_CLASS: Record<CardTone, string> = {
  default: '',
  warn: 'border-l-[3px] border-l-gold-bright',
  crit: 'border-l-[3px] border-l-blood border-blood/40 bg-blood/[0.04]',
  good: 'border-l-[3px] border-l-mint',
};

export default function CustomerCard({ subject, role, tone = 'default', onOpen, testId }: CustomerCardProps) {
  const id = identityDisplay(subject.identity);
  return (
    <article
      data-testid={testId}
      onClick={onOpen}
      className={clsx(
        'panel p-3.5 flex flex-col',
        onOpen && 'cursor-pointer transition hover:border-gold-bright/50',
        TONE_CLASS[tone],
      )}
    >
      {/* Tier 1 · identity */}
      <div className="flex items-center gap-3">
        <span className="shrink-0 w-8 h-8 rounded-full bg-ink-black border border-gold-dim/40 flex items-center justify-center font-serif text-[11px] text-gold-bright">
          {id.initials}
        </span>
        <span
          className={clsx('min-w-0 text-sm text-cream', id.isHandle ? 'font-mono' : 'font-inter')}
          data-testid="cc-name"
        >
          {id.primary}
        </span>
        {id.secondary ? (
          <span className="font-mono text-[11px] text-cream-faint" data-testid="cc-handle">
            {id.secondary}
          </span>
        ) : null}
        {subject.identity.context ? (
          <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-cream-faint">
            {subject.identity.context}
          </span>
        ) : null}
      </div>

      {/* Tier 2 · appointment */}
      <p className="mt-2 font-inter text-[13px] text-cream-soft" data-testid="cc-appointment">
        <span className="text-gold">{format(new Date(subject.startIso), 'EEE d MMM · HH:mm')}</span>
        {subject.artistLabel ? <span className="text-cream-faint"> · {subject.artistLabel}</span> : null}
        {subject.studioLabel ? <span className="text-cream-faint"> · {subject.studioLabel}</span> : null}
      </p>

      {/* Tier 3 · readiness */}
      <div className="mt-2.5 flex gap-1.5 flex-wrap" data-testid="cc-readiness">
        <span className={subject.readiness.deposit.cls}>{subject.readiness.deposit.label}</span>
        <span className={subject.readiness.waiver.cls}>{subject.readiness.waiver.label}</span>
        <span className={subject.readiness.confirm.cls}>{subject.readiness.confirm.label}</span>
      </div>

      {/* Divider + role zone */}
      <div className="divider-gold my-3" />
      <div className="flex items-center gap-3">
        <span className="flex-1 min-w-0 font-serif text-[13px] text-cream-soft" data-testid="cc-reason">
          {role.reason}
        </span>
        {role.actions ? (
          <div className="flex gap-2 shrink-0" data-testid="cc-actions">
            {role.actions}
          </div>
        ) : null}
      </div>
    </article>
  );
}
